import React, { useState, useMemo } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { format } from "date-fns";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  Shield,
} from "lucide-react";

export default function AdminStaff() {
  const [search, setSearch] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();

  const statusConfig = useMemo(() => ({
    ACTIVE: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: t("active") },
    SUSPENDED: { icon: XCircle, color: "bg-red-100 text-red-700", label: t("suspended") },
    INACTIVE: { icon: Clock, color: "bg-amber-100 text-amber-700", label: t("inactive") },
  }), [t, language]);

  const { data: staffUsers = [], isLoading } = useQuery({
    queryKey: ["admin-staff"],
    queryFn: () => adminService.getMyStaffUsers(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminService.updateStaffUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-staff"]);
      setStatusDialog(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminService.deleteStaffUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-staff"]);
      setDeleteDialog(null);
    },
  });

  const filteredStaff = staffUsers.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggleStatus = async () => {
    if (!statusDialog) return;
    const newStatus = statusDialog.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({
      id: statusDialog.id,
      data: { name: statusDialog.name, status: newStatus },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("staffMembers")}</h1>
          <p className="text-slate-500">{staffUsers.length} {t("staffMembersCount")}</p>
        </div>
        <Link to={createPageUrl("AdminStaffForm")}>
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            {t("createStaffMember")}
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t("searchStaff")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("staffMember")}</TableHead>
              <TableHead>{t("roles")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead>{t("created")}</TableHead>
              <TableHead className="text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                  {t("loading")}
                </TableCell>
              </TableRow>
            ) : filteredStaff.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                  {t("noStaffMembersFound")}
                </TableCell>
              </TableRow>
            ) : (
              filteredStaff.map((staff) => {
                const StatusIcon = statusConfig[staff.status]?.icon || Clock;
                const statusColor = statusConfig[staff.status]?.color || "bg-slate-100 text-slate-700";
                
                return (
                  <TableRow key={staff.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{staff.name}</p>
                          <p className="text-sm text-slate-500">{staff.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {staff.roles && staff.roles.length > 0 ? (
                          staff.roles.map((role) => (
                            <Badge key={role.id} variant="outline" className="text-xs">
                              <Shield className="h-3 w-3 mr-1" />
                              {role.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-slate-400">{t("noRolesAssigned")}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColor}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {statusConfig[staff.status]?.label || staff.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {format(new Date(staff.createdAt), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <Link to={createPageUrl("AdminStaffForm") + `?id=${staff.id}`}>
                            <DropdownMenuItem>
                              <Pencil className="h-4 w-4 mr-2" />
                              {t("edit")}
                            </DropdownMenuItem>
                          </Link>
                          <DropdownMenuItem
                            onClick={() => setStatusDialog({
                              id: staff.id,
                              name: staff.name,
                              status: staff.status,
                            })}
                          >
                            {staff.status === "ACTIVE" ? (
                              <>
                                <XCircle className="h-4 w-4 mr-2" />
                                {t("suspend")}
                              </>
                            ) : (
                              <>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                {t("activate")}
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteDialog(staff)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t("delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Status Dialog */}
      <AlertDialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("changeStaffStatus")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("areYouSureYouWantTo")} {statusDialog?.status === "ACTIVE" ? t("suspend").toLowerCase() : t("activate").toLowerCase()} {statusDialog?.name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleToggleStatus}>{t("confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteStaffMember")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteConfirmation")} {deleteDialog?.name}? {t("cannotBeUndone")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(deleteDialog.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}




